<!-- Poster mode: split-screen with image on the left -->
@if (showLoginPoster) {
  <div class="login-split-layout">
    <div class="poster-section" style="background-image: url('assets/creatives/login-image.jpg')"></div>

    <div class="form-section">
      <div class="card pb-6 pt-8 sm:pt-10 px-6 sm:px-10 max-w-[440px] w-full">
        <ng-container *ngTemplateOutlet="loginCard" />
      </div>
    </div>
  </div>
} @else {
  <!-- Default mode: centered card -->
  <div class="flex items-center justify-center p-4 sm:p-8 w-full min-h-screen">
    <div class="card pb-6 pt-8 sm:pt-10 px-6 sm:px-10 max-w-[440px] w-full">
      <ng-container *ngTemplateOutlet="loginCard" />
    </div>
  </div>
}

<!-- =========== SHARED LOGIN CARD CONTENT =========== -->
<ng-template #loginCard>
  <div class="flex justify-center mb-3">
    <div style="height: 48px; max-width: 220px; display: flex; align-items: center; justify-content: center;">
      <img
        [src]="tenantBrandingService.logoUrl() || 'assets/creatives/logo-w70.png'"
        (error)="onLogoError($event)"
        class="signin-logo"
        style="max-height: 100%; max-width: 100%; width: auto; height: auto; object-fit: contain;"
        [alt]="tenantBrandingService.appName() + ' logo'"
      />
    </div>
  </div>

  @if (resetPasswordMode) {
    <p class="text-base font-semibold text-[#1D2433] mb-6 text-center">
      {{ 'Sessions.Login.PasswordChange' | translate }}
    </p>
  } @else if (inviteInfo) {
    <p class="text-xl font-bold text-[#1D2433] mb-2 text-center">Rejoindre {{ inviteInfo.workspaceName }}</p>
    <p class="text-sm text-[#5B6372] mb-6 text-center">Vous avez été invité par {{ inviteInfo.inviterName }}. Connectez-vous pour accepter.</p>
  } @else if (!isForgotPasswordView && !passwordsNeedsRenewal) {
    <p class="text-xl font-bold text-[#1D2433] mb-2 text-center">{{ 'Sessions.Login.WelcomeBack' | translate }}</p>
    <p class="text-sm text-[#5B6372] mb-6 text-center">{{ 'Sessions.Login.PleaseSignIn' | translate }}</p>
  }

  <!-- Render the appropriate view based on the component's state -->
  @if (isForgotPasswordView) {
    <ng-container *ngTemplateOutlet="forgotPasswordView" />
  } @else if (resetPasswordMode || passwordsNeedsRenewal) {
    <ng-container *ngTemplateOutlet="resetPasswordView" />
  } @else {
    <ng-container *ngTemplateOutlet="loginView" />
  }

  <div class="mt-4">
    @if (loading) {
      <mat-progress-bar mode="indeterminate" class="rounded-full"></mat-progress-bar>
    }
  </div>

  <app-language-selector class="mt-6 flex justify-center w-full" />

  <!-- Trust footer -->
  <div class="flex items-center justify-center gap-1.5 mt-6 text-gray-400 text-xs">
    <mat-icon class="text-[14px]! h-3.5! w-3.5!">lock</mat-icon>
    <span>{{ 'Sessions.Login.SecureConnection' | translate }}</span>
  </div>
</ng-template>

<!-- =========== TEMPLATE DEFINITIONS =========== -->

<!--
  Template for the standard login form.
-->
<ng-template #loginView>
  <form [formGroup]="loginForm" class="flex flex-col text-left w-full">
    <div class="w-full mb-2">
      <mat-label class="form-label mb-1">{{ 'Sessions.Login.EmailField' | translate }}</mat-label>
      <mat-form-field subscriptSizing="dynamic" appearance="fill" class="soft-input w-full">
        <input id="email" matInput formControlName="email" type="email" name="email" autocomplete="username" [placeholder]="'Sessions.Login.EmailHere' | translate" />
      </mat-form-field>
    </div>

    <div class="w-full mb-2">
      <mat-label class="form-label mb-1">{{ 'Sessions.Login.PasswordField' | translate }}</mat-label>
      <mat-form-field subscriptSizing="dynamic" appearance="fill" class="soft-input w-full">
        <input
          id="password"
          matInput
          formControlName="password"
          [type]="hide ? 'password' : 'text'"
          name="password"
          autocomplete="current-password"
          [placeholder]="'Sessions.Login.PasswordPlaceholder' | translate"
          (keydown.enter)="onSubmit()"
        />
        <button mat-icon-button matSuffix type="button" (click)="toggleHide()" [attr.aria-label]="'Hide password'" [attr.aria-pressed]="hide">
          <mat-icon>{{ hide ? 'visibility_off' : 'visibility' }}</mat-icon>
        </button>
      </mat-form-field>
    </div>

    <div class="flex justify-end mb-3">
      <a (click)="onForgotMyPasswordEvent()" class="text-sm text-primary hover:text-primary-dark cursor-pointer transition-colors font-medium">
        {{ 'Sessions.Login.ForgotPassWord' | translate }}
      </a>
    </div>

    <button class="w-full py-1 text-sm rounded-lg font-medium mb-1" (click)="onSubmit()" [disabled]="loading || loginForm.invalid" mat-flat-button color="primary">
      {{ 'Sessions.Login.Login' | translate }}
    </button>

    <!-- Or divider -->
    <div class="or-divider flex items-center gap-3 my-3">
      <div class="flex-1 h-px bg-gray-200"></div>
      <span class="text-[13px] text-gray-400 whitespace-nowrap">{{ 'Sessions.Login.OrContinueWith' | translate }}</span>
      <div class="flex-1 h-px bg-gray-200"></div>
    </div>

    <div class="flex flex-col gap-2">
      <button matButton="tonal" class="provider-login w-full border border-gray-200 rounded-lg justify-center flex items-center h-[42px]" [disabled]="loading" (click)="signinWithProvider('google')">
        <img src="assets/images/google.svg" class="h-4 mr-2" />
        <span>{{ 'Sessions.Login.LoginWithGoogle' | translate }}</span>
      </button>

      <button
        matButton="tonal"
        class="provider-login w-full border border-gray-200 rounded-lg justify-center flex items-center h-[42px]"
        [disabled]="loading"
        (click)="signinWithProvider('microsoft')"
      >
        <img src="assets/images/microsoft.svg" class="h-4 mr-2" />
        <span>{{ 'Sessions.Login.LoginWithMicrosoft' | translate }}</span>
      </button>
    </div>

    <!-- Sign up link -->
    <div class="flex justify-center mt-4">
      <span class="text-sm text-gray-500">{{ 'Sessions.Login.NoAccount' | translate }}&nbsp;</span>
      <a routerLink="/signup" queryParamsHandling="merge" class="text-sm font-semibold text-primary hover:text-primary-dark cursor-pointer transition-colors">
        {{ 'Sessions.Login.SignUpLink' | translate }}
      </a>
    </div>
  </form>
</ng-template>

<!--
  Template for the "Forgot Password" flow, where the user requests a password reset link.
-->
<ng-template #forgotPasswordView>
  <form [formGroup]="forgotPasswordForm" class="flex flex-col text-left w-full">
    <p class="text-sm text-[#5B6372] mb-6 text-center">{{ 'Sessions.Login.ForgotPasswordSubtitle' | translate }}</p>

    <div class="w-full mb-2">
      <mat-label class="form-label mb-1">{{ 'Sessions.Login.EmailField' | translate }}</mat-label>
      <mat-form-field subscriptSizing="dynamic" appearance="fill" class="soft-input w-full">
        <input
          id="reset-email"
          matInput
          formControlName="email"
          type="email"
          name="email"
          autocomplete="username"
          [placeholder]="'Sessions.Login.EmailHere' | translate"
          (keydown.enter)="onSubmit()"
        />
      </mat-form-field>
    </div>

    <button class="w-full py-1 text-sm rounded-lg font-medium mb-3 mt-2" (click)="onSubmit()" [disabled]="loading || forgotPasswordForm.invalid" mat-flat-button color="primary">
      {{ 'Sessions.Login.ResetPassword' | translate }}
    </button>

    <div class="flex justify-center mt-2">
      <a (click)="onForgotMyPasswordEvent()" class="text-sm text-gray-500 hover:text-gray-800 cursor-pointer transition-colors flex items-center gap-1">
        <mat-icon class="text-[16px]! h-4! w-4!">arrow_back</mat-icon>
        {{ 'Sessions.Login.BackToSignIn' | translate }}
      </a>
    </div>
  </form>
</ng-template>

<!--
  Template for setting a new password. This is used when completing a password reset
  (coming from an email link) or when the user's password needs to be renewed.
-->
<ng-template #resetPasswordView>
  <form [formGroup]="resetPasswordForm" class="flex flex-col text-left w-full">
    <!-- For renewal, we also ask for the user's current credentials. -->
    @if (passwordsNeedsRenewal) {
      <div class="w-full mb-2">
        <mat-label class="form-label mb-1">{{ 'Sessions.Login.EmailField' | translate }}</mat-label>
        <mat-form-field subscriptSizing="dynamic" appearance="fill" class="soft-input w-full">
          <input id="renewal-email" matInput formControlName="email" type="email" name="email" autocomplete="username" [placeholder]="'Sessions.Login.EmailHere' | translate" />
        </mat-form-field>
      </div>

      <div class="w-full mb-2">
        <mat-label class="form-label mb-1">{{ 'Sessions.Login.PasswordField' | translate }}</mat-label>
        <mat-form-field subscriptSizing="dynamic" appearance="fill" class="soft-input w-full">
          <input
            id="current-password"
            matInput
            formControlName="password"
            [type]="hide ? 'password' : 'text'"
            name="password"
            autocomplete="current-password"
            [placeholder]="'Sessions.Login.PasswordPlaceholder' | translate"
          />
          <button mat-icon-button matSuffix (click)="toggleHide()" [attr.aria-label]="'Hide password'" [attr.aria-pressed]="hide" type="button">
            <mat-icon>{{ hide ? 'visibility_off' : 'visibility' }}</mat-icon>
          </button>
        </mat-form-field>
      </div>
    }

    <!-- New password -->
    <div class="w-full mb-2">
      <mat-label class="form-label mb-1">{{ 'Sessions.Login.NewPasswordInstruction' | translate }}</mat-label>
      <mat-form-field subscriptSizing="dynamic" appearance="fill" class="soft-input w-full">
        <input
          matInput
          formControlName="newPassword"
          [type]="hideNewPassword ? 'password' : 'text'"
          autocomplete="new-password"
          [placeholder]="'Sessions.Login.NewPasswordPlaceholder' | translate"
        />
        <button mat-icon-button matSuffix type="button" (click)="hideNewPassword = !hideNewPassword">
          <mat-icon>{{ hideNewPassword ? 'visibility_off' : 'visibility' }}</mat-icon>
        </button>
      </mat-form-field>
    </div>

    <!-- Password strength meter -->
    @if (resetPasswordForm.get('newPassword')?.value) {
      <div class="password-strength-section mb-3">
        <div class="strength-bar-track">
          @for (i of [0, 1, 2, 3]; track i) {
            <div class="strength-bar-segment" [class]="'strength-' + passwordStrength.level" [class.active]="i < passwordStrength.score"></div>
          }
        </div>
        <span class="strength-label" [class]="'strength-' + passwordStrength.level">{{ passwordStrength.label | translate }}</span>
      </div>

      <!-- Password criteria checklist -->
      <div class="password-criteria mb-4">
        @for (criterion of passwordCriteria; track criterion.key) {
          <div class="criterion-row" [class.met]="criterion.met">
            <mat-icon class="criterion-icon">{{ criterion.met ? 'check_circle' : 'radio_button_unchecked' }}</mat-icon>
            <span>{{ criterion.label | translate }}</span>
          </div>
        }
      </div>
    }

    <!-- Confirm password -->
    <div class="w-full mb-2">
      <mat-label class="form-label mb-1">{{ 'Sessions.Login.ConfirmPasswordLabel' | translate }}</mat-label>
      <mat-form-field subscriptSizing="dynamic" appearance="fill" class="soft-input w-full">
        <input
          matInput
          formControlName="newPasswordConf"
          [type]="hideConfirmPassword ? 'password' : 'text'"
          autocomplete="new-password"
          [placeholder]="'Sessions.Login.ConfirmNewPasswordPlaceholder' | translate"
          (keydown.enter)="onSubmit()"
        />
        <button mat-icon-button matSuffix type="button" (click)="hideConfirmPassword = !hideConfirmPassword">
          <mat-icon>{{ hideConfirmPassword ? 'visibility_off' : 'visibility' }}</mat-icon>
        </button>
      </mat-form-field>
    </div>

    <!-- Mismatch warning -->
    @if (resetPasswordForm.get('newPasswordConf')?.value && !passwordsMatch) {
      <div class="mismatch-warning mb-3">
        <mat-icon class="mismatch-icon">error_outline</mat-icon>
        <span>{{ 'Sessions.Login.PasswordMismatch' | translate }}</span>
      </div>
    }

    <button
      class="w-full py-1 text-sm rounded-lg font-medium"
      (click)="onSubmit()"
      [disabled]="loading || resetPasswordForm.invalid || !passwordsMatch || passwordStrength.score < 3"
      mat-flat-button
      color="primary"
    >
      {{ 'Sessions.Login.SavePassword' | translate }}
    </button>
  </form>
</ng-template>
